import { toCanvas } from 'html-to-image';
import { regionToImageCrop } from './feedbackGeometry.js';

// The only capture pipeline: a one-click DOM render of the app viewport.
// The legacy getDisplayMedia tab-share path (native stream + synthetic and
// native-frame test modes + frame-alignment fallback) was removed once the
// DOM renderer became the default — no share prompt, no stream lifecycle.
const CAPTURE_SOURCE = Object.freeze({
  dom: 'dom-render',
});

const FEEDBACK_CAPTURE_CHROME_SELECTOR = [
  '.feedback-overlay',
  '.feedback-launcher-wrap',
  '.feedback-menu',
  '.feedback-region-layer',
  '.feedback-element-selector',
  '.feedback-session-bar',
  '.feedback-modal-backdrop',
].join(',');

// html-to-image clones the app-shell subtree, not its body ancestor. An
// explicit clone filter is therefore the authoritative evidence boundary;
// the transient body class remains a useful live-page visual safeguard.
export function feedbackCaptureNodeFilter(node) {
  return !(node instanceof Element && node.matches(FEEDBACK_CAPTURE_CHROME_SELECTOR));
}

function waitForPaint() {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );
}

async function drawBase64ImageCanvas(base64) {
  if (!base64) return null;
  const image = await loadImageElement(`data:image/png;base64,${base64}`);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width || window.innerWidth;
  canvas.height = image.naturalHeight || image.height || window.innerHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load feedback capture image.'));
    image.src = src;
  });
}

function estimateDecodedBytes(dataUrl) {
  const base64 = String(dataUrl || '').split(',')[1] || '';
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

function scaledDimensions(width, height, scale) {
  return {
    width: Math.max(1, Math.round((Number(width) || 1) * scale)),
    height: Math.max(1, Math.round((Number(height) || 1) * scale)),
  };
}

async function redrawDataUrl(dataUrl, scale, { type = 'image/png', quality } = {}) {
  const image = await loadImageElement(dataUrl);
  const dimensions = scaledDimensions(
    image.naturalWidth || image.width,
    image.naturalHeight || image.height,
    scale,
  );
  const canvas = document.createElement('canvas');
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL(type, quality);
}

// The DEFAULT budget is derived from the strictest server limit, not the
// screenshot cap: the mandatory annotatedSvg embeds the same raster as base64
// text and is bounded at FEEDBACK_MAX_SVG_BYTES (2 MiB of TEXT by default).
// base64 inflates decoded bytes by 4/3, so the decoded screenshot must stay
// under roughly 1.5 MB for the SVG to fit — with margin for SVG markup and
// JSON escaping, 1.4 MB decoded is the honest ceiling. A 6 MB budget passed
// captures that the server then rejected on every autosave.
export const CAPTURE_BUDGET_MAX_BYTES = 1_400_000;

// Fail-closed capture sizing (round-2 WS-I): a capture that cannot be brought
// under budget is REJECTED at capture time, never returned as success — an
// over-budget "success" opens a workspace whose every autosave then 413s, a
// silent dead-end for the tester.
export class CaptureTooLargeError extends Error {
  constructor(message, { finalBytes = null, maxBytes = null, cause = null } = {}) {
    super(message);
    this.name = 'CaptureTooLargeError';
    this.code = 'CAPTURE_TOO_LARGE';
    this.finalBytes = finalBytes;
    this.maxBytes = maxBytes;
    if (cause) this.cause = cause;
  }
}

export async function enforceCaptureBudget(
  dataUrl,
  { maxBytes = CAPTURE_BUDGET_MAX_BYTES, minScale = 0.5, step = 0.85 } = {},
) {
  const originalBytes = estimateDecodedBytes(dataUrl);
  if (!dataUrl || originalBytes <= maxBytes) {
    return {
      dataUrl,
      scale: 1,
      originalBytes,
      finalBytes: originalBytes,
    };
  }

  let scale = 1;
  let finalDataUrl = dataUrl;
  let finalBytes = originalBytes;
  try {
    while (finalBytes > maxBytes && scale > minScale) {
      scale = Math.max(minScale, scale * step);
      finalDataUrl = await redrawDataUrl(dataUrl, scale, { type: 'image/png' });
      finalBytes = estimateDecodedBytes(finalDataUrl);
      if (scale === minScale) break;
    }
    if (finalBytes > maxBytes && scale <= minScale) {
      // Walk the JPEG quality ladder until the payload fits — a single pass at
      // 0.85 can still exceed the budget for dense screenshots.
      for (const quality of [0.85, 0.75, 0.65, 0.55]) {
        const candidate = await redrawDataUrl(dataUrl, scale, { type: 'image/jpeg', quality });
        const candidateBytes = estimateDecodedBytes(candidate);
        if (candidateBytes < finalBytes) {
          finalDataUrl = candidate;
          finalBytes = candidateBytes;
        }
        if (finalBytes <= maxBytes) break;
      }
    }
  } catch (error) {
    // A failed redraw must never fall back to the unbounded original.
    throw new CaptureTooLargeError('This capture could not be compressed for saving.', {
      finalBytes: originalBytes,
      maxBytes,
      cause: error,
    });
  }
  if (finalBytes > maxBytes) {
    throw new CaptureTooLargeError('This capture is too large to save even after compression.', {
      finalBytes,
      maxBytes,
    });
  }
  return {
    dataUrl: finalDataUrl,
    scale,
    originalBytes,
    finalBytes,
  };
}

// Every window.__feedback* capture seam is test-only: in a real environment a
// stray global must never substitute or fail evidence captures.
function captureSeamsEnabled() {
  const env = import.meta.env || {};
  return Boolean(env.DEV || env.VITE_E2E === 'true' || env.VITE_E2E === true);
}

// Test-only breadcrumbs (same gate as the seams themselves): the capture chain
// crosses an exposeFunction boundary in e2e, and when it stalls the only
// observable state is "Preparing screenshot…". These console marks let a trace
// say WHERE it stalled.
function seamLog(step) {
  if (captureSeamsEnabled()) console.info(`[feedback-capture-seam] ${step}`);
}

async function drawViewportHookCanvas() {
  if (!captureSeamsEnabled()) return null;
  const fallbackBase64 = window.__feedbackAppViewportFallbackPng || null;
  if (fallbackBase64) {
    window.__feedbackAppViewportFallbackPng = null;
    return drawBase64ImageCanvas(fallbackBase64);
  }
  const captureViewport = window.__feedbackCaptureViewportPng;
  if (typeof captureViewport !== 'function') return null;
  seamLog('hook-invoke');
  const base64 = await captureViewport();
  seamLog(`hook-returned bytes=${String(base64 || '').length}`);
  const canvas = await drawBase64ImageCanvas(base64);
  seamLog('hook-canvas-drawn');
  return canvas;
}

async function drawAppViewportCanvas(viewport) {
  if (captureSeamsEnabled() && window.__feedbackAppViewportCaptureShouldFail) {
    window.__feedbackAppViewportCaptureShouldFail = false;
    throw new Error('App viewport capture failed.');
  }

  const exactCanvas = await drawViewportHookCanvas();
  if (exactCanvas) return exactCanvas;

  // Known tech debt (codex-audit P1-2): toCanvas rasterizes the full shell
  // height (min-height:100vh means long pages render at document height)
  // before the viewport slice is copied out. Very long routes pay a large
  // intermediate canvas. Clipping inside toCanvas via transform risks losing
  // fixed-position chrome, so it needs its own fidelity pass — tracked, not
  // changed as part of the tab-share removal.
  const shell = document.querySelector('[data-testid="app-shell"]') || document.body;
  const rect = shell.getBoundingClientRect();
  const rendered = await toCanvas(shell, {
    cacheBust: true,
    pixelRatio: 1,
    skipAutoScale: true,
    width: Math.max(1, Math.round(rect.width)),
    height: Math.max(1, Math.round(rect.height)),
    canvasWidth: Math.max(1, Math.round(rect.width)),
    canvasHeight: Math.max(1, Math.round(rect.height)),
    filter: feedbackCaptureNodeFilter,
    style: {
      margin: '0',
      transform: 'none',
      transformOrigin: 'top left',
    },
  });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  const sourceX = Math.max(0, Math.round(-rect.left));
  const sourceY = Math.max(0, Math.round(-rect.top));
  const targetX = Math.max(0, Math.round(rect.left));
  const targetY = Math.max(0, Math.round(rect.top));
  const width = Math.max(0, Math.min(rendered.width - sourceX, canvas.width - targetX));
  const height = Math.max(0, Math.min(rendered.height - sourceY, canvas.height - targetY));
  if (width <= 0 || height <= 0) {
    throw new Error('App viewport capture failed.');
  }
  ctx.drawImage(rendered, sourceX, sourceY, width, height, targetX, targetY, width, height);
  return canvas;
}

function cropCanvas(source, region, viewport) {
  if (!region) return source;
  const crop = regionToImageCrop(region, viewport, { width: source.width, height: source.height });
  const canvas = document.createElement('canvas');
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(source, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  return canvas;
}

// One-click capture: renders the app viewport directly (html-to-image), no
// getDisplayMedia stream and no browser share prompt. Deterministic in tests
// via the __feedbackCaptureViewportPng / __feedbackAppViewportFallbackPng
// hooks; __feedbackAppViewportCaptureShouldFail simulates a render failure.
export async function captureFrameFromDom({
  region = null,
  maxBytes = CAPTURE_BUDGET_MAX_BYTES,
} = {}) {
  seamLog('frame-start');
  document.body.classList.add('feedback-capture-hiding-chrome');
  await waitForPaint();
  seamLog('frame-painted');
  try {
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
      scrollX: window.scrollX || 0,
      scrollY: window.scrollY || 0,
    };
    const source = await drawAppViewportCanvas(viewport);
    seamLog('frame-source-ready');
    const canvas = cropCanvas(source, region, viewport);
    const constrainedCapture = await enforceCaptureBudget(canvas.toDataURL('image/png'), {
      maxBytes,
    });
    // Defensive backstop: enforceCaptureBudget already fails closed, but the
    // caller must never accept an over-budget payload from any future path.
    if (constrainedCapture.finalBytes > maxBytes) {
      throw new CaptureTooLargeError('This capture is too large to save.', {
        finalBytes: constrainedCapture.finalBytes,
        maxBytes,
      });
    }
    const image = scaledDimensions(canvas.width, canvas.height, constrainedCapture.scale);
    return {
      screenshotDataUrl: constrainedCapture.dataUrl,
      image,
      viewport,
      captureSource: CAPTURE_SOURCE.dom,
      fallbackReason: null,
      sourceFrame: null,
      compression:
        constrainedCapture.scale < 1
          ? {
              scale: constrainedCapture.scale,
              originalBytes: constrainedCapture.originalBytes,
              finalBytes: constrainedCapture.finalBytes,
            }
          : undefined,
    };
  } finally {
    document.body.classList.remove('feedback-capture-hiding-chrome');
  }
}
